import React, { useCallback, useState, useLayoutEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import * as Mui from '@material-ui/core';
import {
  MoreVert as IconMore,
  Delete as IconDelete,
  Cancel as IconCancel,
  Edit as IconEdit,
  LocalAtm as IconLocalAtm,
  SyncAlt as IconSyncAlt,
  OpenInNew as IconOpenInNew
} from '@material-ui/icons';
import { TIPO_DOCUMENTO } from '~/constants';
import { formatCurrency } from '~/utils/format';
import { getApplicationIsOnline, isWeb } from '~/store/modules/app/app-selectors';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';
import CartEmpty from '~/components/CartEmpty';
import ModalCondicaoPagamentoProduto from '../../modal/ModalCondicaoPagamentoProduto';
import ModalSaldoEstoqueProduto from '../../modal/ModalSaldoEstoqueProduto';
import ModalConversorUnidadeMedida from '../../modal/ModalConversorUnidadeMedida';
import ModalEditarQuantidade from '../../modal/ModalEditarQuantidade';
import { selectorParametroAsBoolean, selectorRotina } from '~/store/modules/auth/auth-selectors';
import { usePreferencia } from '~/store/modules/preferencias/preferencias-selectors';
import { List, useListRef } from 'react-window';
import InfoPopover from '~/components/InfoPopover';


const MAX_LINHAS_TITULO_ITEM = 2
const LINE_HEIGHT_TITULO_ITEM = 24
const MAX_LINHAS_INFOS_ITEM = 3
const LINE_HEIGHT_INFOS_ITEM = 21
const PADDING_VERTICAL_ITEM = 6
const GAP_ITEM_DESCRICAO = 1
const ITEM_ROW_HEIGHT = (
  (LINE_HEIGHT_INFOS_ITEM * MAX_LINHAS_INFOS_ITEM) +
  (LINE_HEIGHT_TITULO_ITEM * MAX_LINHAS_TITULO_ITEM) +
  (PADDING_VERTICAL_ITEM * 2) +
  (GAP_ITEM_DESCRICAO)
)

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: '0 6px 0  0'
  },
  container: {
    flex: 1,
    height: '100%'
  },
  produtosMoreInfosLineInfo: {
    fontSize: 14,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  produtosMoreInfosBaseCard: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '1px 7px',
    borderRadius: '12px',
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerRow: {
    gap: "4px",
    display: "grid",
    gridTemplateColumns: "1fr 7fr 2fr 2fr",
    alignItems: "center",
    padding: "16px 8px",
    paddingRight: "24px",
    boxSizing: "border-box",
  },
  row: {
    gap: "4px",
    display: "grid",
    gridTemplateColumns: "1fr 7fr 2fr 2fr",
    alignItems: "center",
    padding: `${PADDING_VERTICAL_ITEM}px 8px`,
    boxSizing: "border-box",
    position: "relative",
    borderBottom: `1px solid ${theme.palette.listItem.row.separatorColor}`,

    "&:hover": {
      backgroundColor: theme.palette.listItem.row.hoverColor,
    },
  },
  rowFirst: {
    borderTop: `1px solid ${theme.palette.listItem.row.separatorColor}`,
  },
  colItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  colDescricao: {
    overflow: "hidden",
    whiteSpace: "normal",
    wordBreak: "break-word",
    textOverflow: "ellipsis",
    display: 'flex',
    flexDirection: 'column',
    gap: `${GAP_ITEM_DESCRICAO}px`,
  },
  itemTitulo: {
    display: '-webkit-box',
    WebkitLineClamp: MAX_LINHAS_TITULO_ITEM,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    maxHeight: LINE_HEIGHT_TITULO_ITEM * MAX_LINHAS_TITULO_ITEM,
    fontSize: 16,
    lineHeight: `${LINE_HEIGHT_TITULO_ITEM}px`,

    fontWeight: 600,
    textTransform: 'uppercase',
  },
  itemInfos: {
    overflowY: "auto",
    overflowX: "hidden",
    whiteSpace: "normal",
    wordBreak: "break-word",

    maxHeight: LINE_HEIGHT_INFOS_ITEM * MAX_LINHAS_INFOS_ITEM,
    fontSize: 14,
    lineHeight: `${LINE_HEIGHT_INFOS_ITEM}px`,
  },
  colValor: {
    display: "flex",
    justifyContent: "flex-end",
    overflow: "hidden",
  },
  colAcoes: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "2px",
    flexWrap: "wrap",
    maxWidth: "100%",
  },
  customScroll: {
    overflow: "auto",

    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.listItem.scrollbar.bgColor1} transparent`,

    '&:hover': {
      scrollbarColor: `${theme.palette.listItem.scrollbar.bgColor3} transparent`,
    },
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 8,
      background: theme.palette.listItem.scrollbar.bgColor1,
      transition: 'background 0.25s ease, opacity 0.25s ease',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      background: theme.palette.listItem.scrollbar.bgColor2,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.listItem.scrollbar.bgColor3,
    },
  },
}));

const MODAL_TYPES = {
  SALDO_ESTOQUE_PRODUTO: 'saldo_estoque_produto',
  CONDICAO_PAGAMENTO_PRODUTO: 'condicao_pagamento_produto',
  CONVERSOR_UNIDADE_MEDIDA: 'conversor_unidade_medida',
  EDITAR_QUANTIDADE: 'editar_quantidade'
};

const ListaItens = () => {
  const dispatch = useDispatch();
  const isOnline = useSelector(getApplicationIsOnline());
  const { empresa } = useSelector(state => state?.auth);
  const theme = useTheme();
  const classes = useStyles();

  const [showModalCondicaoPagamento, setShowModalCondicaoPagamento] = useState(false);
  const [showModalSaldoEstoque, setShowModalSaldoEstoque] = useState(false);
  const [showModalConversorUnidadeMedida, setShowModalConversorUnidadeMedida] = useState(false);
  const [showModalEditarQuantidade, setShowModalEditarQuantidade] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { venda, tipoDocumento } = useSelector(state => state.pdv);

  const { listaItensScrollCommand, listaItensLastVisibleIndex } = useSelector(state => state.pdv);

  const parametroPF050 = useSelector(selectorParametroAsBoolean('PF050'))
  const parametroPF021 = useSelector(selectorParametroAsBoolean('PF021'));
  const parametroPC009 = useSelector(selectorParametroAsBoolean('PC009'));
  // const parametroPF049 = useSelector(selectorParametroAsBoolean('PF049'));

  const permissaoUsuarioCancelarItemOrcamento = useSelector(selectorRotina('F051ORC'));
  const permissaoUsuarioCancelarItemPedido = useSelector(selectorRotina('F061PED'));

  const exibirAcrescimoPercentual = usePreferencia('exibirAcrescimoPercentual');
  const exibirAcrescimoValor = usePreferencia('exibirAcrescimoValor');
  const exibirAmbiente = usePreferencia('exibirAmbiente');
  const exibirCodigoBarras = usePreferencia('exibirCodigoBarras');
  const exibirDescontoPercentual = usePreferencia('exibirDescontoPercentual');
  const exibirDescontoValor = usePreferencia('exibirDescontoValor');
  const exibirEstoque = usePreferencia('exibirEstoque');
  const exibirValorBruto = usePreferencia('exibirValorBruto');

  const itens = useSelector(state => state.pdv.venda.itens);
  const itensFiltrados = useMemo(() => {
    return itens?.filter(item => !parametroPF050 || !item.itemCancelado);
  }, [itens, parametroPF050]);

  const [modalState, setModalState] = useState({
    tipo: null,
    item: null
  });

  const openModal = useCallback((tipo, item) => {
    setModalState({ tipo, item });
  }, []);

  const closeModal = () => {
    setModalState({ tipo: null, item: null });
  };

  const listRef = useListRef(null);

  useLayoutEffect(() => {
    if (!listaItensScrollCommand) return;
    if (!listRef.current) return;

    switch (listaItensScrollCommand.type) {
      case 'LAST':
        listRef.current.scrollToRow({
          index: itensFiltrados.length - 1,
          align: 'end',
          behavior: 'smooth',
        });
        break;

      case 'INDEX':
        listRef.current.scrollToRow({
          index: listaItensScrollCommand.index,
          align: 'auto',
          behavior: 'smooth',
        });
        break;

      case 'RESTORE':
        const indexToRestore = listaItensLastVisibleIndex ?? 0;
        listRef.current.scrollToRow({
          index: indexToRestore,
          align: 'auto',
          behavior: 'smooth',
        });
        break;
    }

    dispatch(pdvActions.setResetScrollCommand());

  }, [listaItensScrollCommand, itensFiltrados.length]);

  const RowVirtual = ({ index, style, itensFiltrados }) => {
    const item = itensFiltrados?.[index];
    if (!item) return null;

    return (
      <div style={style}>
        <ItemVendido
          {...itemProps}
          index={index}
          item={item}
        />
      </div>
    );
  };


  const itemProps = React.useMemo(() => ({
    vendaImportado: venda.importado,
    vendaEditar: venda.editar,
    tipoDocumento,
    empresa,
    theme,
    classes,
    openModal,
    parametroPF021,
    parametroPC009,
    permissaoUsuarioCancelarItemOrcamento,
    permissaoUsuarioCancelarItemPedido,
    exibirAcrescimoPercentual,
    exibirAcrescimoValor,
    exibirAmbiente,
    exibirCodigoBarras,
    exibirDescontoPercentual,
    exibirDescontoValor,
    exibirEstoque,
    exibirValorBruto
  }), [
    venda.importado,
    venda.editar,
    tipoDocumento,
    empresa,
    theme,
    classes,
    openModal,
    parametroPF021,
    parametroPC009,
    permissaoUsuarioCancelarItemOrcamento,
    permissaoUsuarioCancelarItemPedido,
    exibirAcrescimoPercentual,
    exibirAcrescimoValor,
    exibirAmbiente,
    exibirCodigoBarras,
    exibirDescontoPercentual,
    exibirDescontoValor,
    exibirEstoque,
    exibirValorBruto
  ]);

  return (
    <>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className={classes.root}>
          <div className={classes.headerRow}>
            <div style={{ textAlign: 'center' }}>Item</div>
            <div style={{ textAlign: 'left' }}>Descrição (EAN/GTIN)</div>
            <div style={{ textAlign: 'right' }}>Valor Total</div>
            <div />
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          {itensFiltrados.length === 0 ? (
            <CartEmpty />
          ) : (
            <List
              listRef={listRef}
              className={classes.customScroll}
              rowComponent={RowVirtual}
              rowCount={itensFiltrados.length}
              rowHeight={ITEM_ROW_HEIGHT}
              rowProps={{ itensFiltrados }}

            />
          )}
        </div>

      </div>

      {modalState.tipo === MODAL_TYPES.SALDO_ESTOQUE_PRODUTO && (
        <ModalSaldoEstoqueProduto
          open={true}
          data={{
            produto: modalState.item?.produtoEstProduto,
            saldoEstoque: modalState.item?.saldoEstoque
          }}
          onClose={closeModal}
        />
      )}

      {modalState.tipo === MODAL_TYPES.CONDICAO_PAGAMENTO_PRODUTO && (
        <ModalCondicaoPagamentoProduto
          open={true}
          data={{
            produto: modalState.item?.produtoEstProduto,
            precoVenda: modalState.item?.produtoPrecoVenda
          }}
          onClose={closeModal}
        />
      )}

      {modalState.tipo === MODAL_TYPES.CONVERSOR_UNIDADE_MEDIDA && (
        <ModalConversorUnidadeMedida
          open={true}
          data={{
            produto: modalState.item?.produtoEstProduto,
            quantidade: modalState.item?.itemQuantidade,
            unidade: modalState.item?.produtoUnidadeComercial,
            valorUnitario: modalState.item?.itemValorUnitario
          }}
          onClose={closeModal}
        />
      )}

      {modalState.tipo === MODAL_TYPES.EDITAR_QUANTIDADE && (
        <ModalEditarQuantidade
          open={true}
          data={{
            produto: modalState.item?.produtoEstProduto,
            quantidade: modalState.item?.itemQuantidade,
            itemVenda: modalState.item
          }}
          onClose={closeModal}
        />
      )}

    </>

  );
};

const ItemVendido = React.memo(({
  index,
  item,
  vendaImportado,
  vendaEditar,
  tipoDocumento,
  empresa,
  theme,
  classes,
  openModal,
  parametroPF021,
  parametroPC009,
  permissaoUsuarioCancelarItemOrcamento,
  permissaoUsuarioCancelarItemPedido,
  exibirAcrescimoPercentual,
  exibirAcrescimoValor,
  exibirAmbiente,
  exibirCodigoBarras,
  exibirDescontoPercentual,
  exibirDescontoValor,
  exibirEstoque,
  exibirValorBruto,
}) => {
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);

  let estoqueCalculado = item?.itemEstoque;

  if(!estoqueCalculado && vendaImportado) {
    if(item?.produtoEstProduto?.saldoEstoque)
      estoqueCalculado = item?.produtoEstProduto?.saldoEstoque?.estoqueReal - item?.produtoEstProduto?.saldoEstoque?.estoqueReservado + item?.itemQuantidade;
    else if(item?.produtoEstProduto?.estSaldoestoque) {
       const getArmazem = item?.produtoEstProduto?.estSaldoestoque.filter(i => i.estArmazemId === item.produtoEstArmazemId);
       estoqueCalculado = getArmazem.reduce((total, saldo) => total + saldo.estoqueReal - saldo.estoqueReservado, 0);
    }
  }

  let estoqueExibicao = estoqueCalculado;
  
  if (item?.produtoEstArmazemId && item?.produtoEstArmazem) {
    if (item?.saldoEstoque) {
      if (Array.isArray(item.saldoEstoque)) {
        const armazemSelecionado = item?.saldoEstoque.find(
          saldo => saldo.estArmazemId === item.produtoEstArmazemId
        );
        if (armazemSelecionado) {
          estoqueExibicao = armazemSelecionado.estoqueReal - armazemSelecionado.estoqueReservado;
        }
      } else {
        estoqueExibicao = item?.saldoEstoque?.estoqueReal - item?.saldoEstoque?.estoqueReservado;
      }
    } else if (item?.produtoEstProduto?.estSaldoestoque) {
      const armazemSelecionado = item?.produtoEstProduto?.estSaldoestoque.find(
        saldo => saldo.estArmazemId === item.produtoEstArmazemId
      );
      if (armazemSelecionado) {
        estoqueExibicao = armazemSelecionado.estoqueReal - armazemSelecionado.estoqueReservado;
      }
    }
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditarQuantidade = () => {
    handleClose();
    if (!item?.id) {
      dispatch(pdvActions.editarItemVenda(item));
    } else if (!item?.itemCancelado) {
      openModal(MODAL_TYPES.EDITAR_QUANTIDADE, item);
    }
  };

  const produtoImposto = item?.produtoEstProduto?.estProdutoImposto?.find(i => i.intEmpresaId === empresa?.id);
  const permiteVenda = produtoImposto?.permiteVenda || item?.produtoEstProduto?.estProdutoImposto[0]?.permiteVenda;

  const itemPercentualDesconto = vendaEditar || vendaImportado ? (parseFloat((item?.itemValorDesconto / (item?.itemValorUnitario * item?.itemQuantidade)) * 100) || 0) : 0;
  const itemPercentualAcrescimo = parseFloat((item?.itemValorAcrescimo / (item?.itemValorUnitario * item?.itemQuantidade)) * 100) || 0

  const getComparisonValue = (item) => {
    return item.produtoPrecoVenda ?? item.produtoEstProduto?.itemValorUnitario ?? item.itemValorPrecoVendaOriginal;
  };
  const comparisonValue = getComparisonValue(item);
  const isPriceLower = item.itemValorUnitario < comparisonValue;

  return (
    <div style={{height: '100%'}} className={`${classes.row} ${index === 0 ? classes.rowFirst : ''}`}>

      {/* NUMERO ITEM */}
      <div className={classes.colItem}>
        <Typography variant="h4" align="center">
          {index + 1}
        </Typography>
      </div>

      <div className={classes.colDescricao}>
        {/* TÍTULO */}
        <Typography
          className={classes.itemTitulo}
          style={{textDecoration: item?.itemCancelado ? 'line-through' : 'normal',}}
        >
          <InfoPopover showDelay={1500} hideDelay={100} content={<b>{item?.produtoCodigo} - {item?.produtoDescricao}</b>}>
            <b>{item?.produtoCodigo} - {item?.produtoDescricao}</b>
          </InfoPopover>
        </Typography>
        {/* INFOS */}
        <div className={`${classes.itemInfos} ${classes.customScroll}`}>
          <InfosProduto
            item={item}
            classes={classes}
            theme={theme}
            parametroPF021={parametroPF021}
            parametroPC009={parametroPC009}
            exibirAcrescimoPercentual={exibirAcrescimoPercentual}
            exibirAcrescimoValor={exibirAcrescimoValor}
            exibirAmbiente={exibirAmbiente}
            exibirCodigoBarras={exibirCodigoBarras}
            exibirDescontoPercentual={exibirDescontoPercentual}
            exibirDescontoValor={exibirDescontoValor}
            exibirEstoque={exibirEstoque}
            exibirValorBruto={exibirValorBruto}
            permiteVenda={permiteVenda}
            estoqueExibicao={estoqueExibicao}
            itemPercentualDesconto={itemPercentualDesconto}
            itemPercentualAcrescimo={itemPercentualAcrescimo}
            isPriceLower={isPriceLower}
            formatCurrency={formatCurrency}
          />
        </div>
      </div>


      {/* VALOR */}
      <div className={classes.colValor}>
        <Typography align="right" style={{ fontWeight: 600 }}>
          {formatCurrency(item?.itemValorTotal)}
        </Typography>
      </div>

      {/* ACOES */}
      <div className={classes.colAcoes}>
        {!item?.id && (
          <Tooltip title="Excluir">
            <IconButton
              disableRipple
              aria-controls="simple-menu"
              aria-haspopup="true"
              aria-label={'Excluir'}
              onClick={() => dispatch(pdvActions.removerItem(item))}
            >
              <IconDelete />
            </IconButton>
          </Tooltip>
        )}

        {(item?.id > 0) && ((
            (tipoDocumento === TIPO_DOCUMENTO.ORCAMENTO && permissaoUsuarioCancelarItemOrcamento) ||
            (tipoDocumento === TIPO_DOCUMENTO.PEDIDO && permissaoUsuarioCancelarItemPedido) ||
            (tipoDocumento === TIPO_DOCUMENTO.DOCUMENTOSAIDA)
          ) && (
            <Tooltip title="Cancelar">
              <IconButton
                disableRipple
                aria-controls="simple-menu"
                aria-haspopup="true"
                aria-label={'Cancelar'}
                onClick={() => dispatch(pdvActions.cancelarItem(item))}
              >
                <IconCancel />
              </IconButton>
            </Tooltip>
          )
        )}

        <Tooltip title="Mais ações">
          <IconButton
            disableRipple
            aria-controls="simple-menu"
            aria-haspopup="true"
            aria-label={'Mais ações'}
            onClick={handleClick}
          >
            <IconMore />
          </IconButton>
        </Tooltip>

        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {tipoDocumento !== !item?.itemCancelado && (
            <MenuItem onClick={() => {
                handleClose();
                handleEditarQuantidade();
              }}
            >
              <IconEdit />
              <Mui.Typography variant="inherit" component="span" style={{ paddingLeft: 8 }}>
                Editar
              </Mui.Typography>
            </MenuItem>
          )}
          {isWeb && (
            <MenuItem
              onClick={() => {
                handleClose();
                openModal(MODAL_TYPES.SALDO_ESTOQUE_PRODUTO, item);
              }}
            >
              <IconOpenInNew />
              <Mui.Typography variant="inherit" component="span" style={{ paddingLeft: 8 }}>
                Saldo estoque
              </Mui.Typography>
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              handleClose();
              openModal(MODAL_TYPES.CONDICAO_PAGAMENTO_PRODUTO, item);
            }}
          >
            <IconLocalAtm />
            <Mui.Typography variant="inherit" component="span" style={{ paddingLeft: 8 }}>
              Condições de pagamento
            </Mui.Typography>
          </MenuItem>
          {isWeb && (
            <MenuItem
              onClick={() => {
                handleClose();
                openModal(MODAL_TYPES.CONVERSOR_UNIDADE_MEDIDA, item);
              }}
            >
              <IconSyncAlt />
              <Mui.Typography variant="inherit" component="span" style={{ paddingLeft: 8 }}>
                Converter unidade de medida
              </Mui.Typography>
            </MenuItem>
          )}
        </Menu>
      </div>
    </div>
  );
});

const InfosProduto = React.memo(({
  item,
  classes,
  theme,
  parametroPF021,
  parametroPC009,
  exibirAcrescimoPercentual,
  exibirAcrescimoValor,
  exibirAmbiente,
  exibirCodigoBarras,
  exibirDescontoPercentual,
  exibirDescontoValor,
  exibirEstoque,
  exibirValorBruto,
  permiteVenda,
  estoqueExibicao,
  itemPercentualDesconto,
  itemPercentualAcrescimo,
  isPriceLower,
  formatCurrency
}) => {

  const elementos = React.useMemo(() => {
    const arr = [];

    // AMBIENTE
    if (
      exibirAmbiente &&
      parametroPC009 &&
      item?.estProdutoAmbiente?.descricao &&
      item?.estProdutoAmbiente?.descricao !== 'NENHUM'
    ) {
      arr.push(
        <Box
          key="ambiente"
          component="span"
          className={classes.produtosMoreInfosBaseCard}
        >
          {item.estProdutoAmbiente.descricao}
        </Box>
      );
    }

    // ESTOQUE INSUFICIENTE
    if (!parametroPF021 && item?.estoqueInsuficiente) {
      arr.push(
        <Box
          key="estoqueInsuficiente"
          component="span"
          className={classes.produtosMoreInfosBaseCard}
          style={{
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
          }}
        >
          ESTOQUE INSUFICIENTE
        </Box>
      );
    }

    // NÃO PERMITE VENDA
    if (!permiteVenda) {
      arr.push(
        <Box
          key="naoPermiteVenda"
          component="span"
          className={classes.produtosMoreInfosBaseCard}
          style={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          }}
        >
          NÃO PERMITE VENDA
        </Box>
      );
    }

    // QUANTIDADE
    if (item?.itemQuantidade && item?.produtoUnidadeComercial) {
      arr.push(
        <Box key="qtd" component="span" className={classes.produtosMoreInfosLineInfo}>
          Qtd: <b>{item.itemQuantidade} {item.produtoUnidadeComercial}</b>
        </Box>
      );
    }

    // VALOR BRUTO
    if (exibirValorBruto && item?.itemValorUnitario != null) {
      arr.push(
        <Box
          key="valorBruto"
          component="span"
          className={
            isPriceLower
              ? classes.produtosMoreInfosBaseCard
              : classes.produtosMoreInfosLineInfo
          }
        >
          Vl.Bruto: <b>{formatCurrency(item.itemValorUnitario)}</b>
        </Box>
      );
    }

    // VALOR UNITÁRIO
    if (item?.itemValorTotal && item?.itemQuantidade) {
      arr.push(
        <Box key="valorUnit" component="span" className={classes.produtosMoreInfosLineInfo}>
          Vl.Unit: <b>{formatCurrency(item.itemValorTotal / item.itemQuantidade)}</b>
        </Box>
      );
    }

    // DESCONTO
    if (
      item?.itemValorDesconto != null &&
      (exibirDescontoPercentual || exibirDescontoValor)
    ) {
      let texto = '';

      if (exibirDescontoPercentual) {
        texto += `${(itemPercentualDesconto ?? 0).toFixed(2)}%`;
      }

      if (exibirDescontoValor) {
        texto += texto ? ' - ' : '';
        texto += formatCurrency(item.itemValorDesconto);
      }

      arr.push(
        <Box key="desconto" component="span" className={classes.produtosMoreInfosLineInfo}>
          Desc: <b>{texto}</b>
        </Box>
      );
    }

    // ACRÉSCIMO
    if (
      item?.itemValorAcrescimo != null &&
      (exibirAcrescimoPercentual || exibirAcrescimoValor)
    ) {
      let texto = '';

      if (exibirAcrescimoPercentual) {
        texto += `${(itemPercentualAcrescimo ?? 0).toFixed(2)}%`;
      }

      if (exibirAcrescimoValor) {
        texto += texto ? ' - ' : '';
        texto += formatCurrency(item.itemValorAcrescimo);
      }

      arr.push(
        <Box key="acrescimo" component="span" className={classes.produtosMoreInfosLineInfo}>
          Acrésc: <b>{texto}</b>
        </Box>
      );
    }

    // ESTOQUE
    if (exibirEstoque && estoqueExibicao != null && !Number.isNaN(estoqueExibicao)) {
      arr.push(
        <Box key="estoque" component="span" className={classes.produtosMoreInfosLineInfo}>
          Est: <b>{estoqueExibicao}</b>
        </Box>
      );
    }

    // EAN
    if (exibirCodigoBarras && item?.produtoEan) {
      arr.push(
        <Box key="ean" component="span" className={classes.produtosMoreInfosLineInfo}>
          EAN: <b>{item.produtoEan}</b>
        </Box>
      );
    }

    return arr;
  }, [
    item,
    classes,
    theme,
    parametroPF021,
    parametroPC009,
    exibirAcrescimoPercentual,
    exibirAcrescimoValor,
    exibirAmbiente,
    exibirCodigoBarras,
    exibirDescontoPercentual,
    exibirDescontoValor,
    exibirEstoque,
    exibirValorBruto,
    permiteVenda,
    estoqueExibicao,
    itemPercentualDesconto,
    itemPercentualAcrescimo,
    isPriceLower,
    formatCurrency
  ]);

  return (
    <>
      {elementos.map((element, index) => (
        <React.Fragment key={index}>
          {element}
          {index < elementos.length - 1 && (
            <Box component="span"><b> | </b></Box>
          )}
        </React.Fragment>
      ))}
    </>
  );
});

export default ListaItens;